import { ExternalLink, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OrganizationActivity, TaskWithContext } from '@/types/adminActivity';

function getTaskStateBadgeClass(state: TaskWithContext['state']): string {
  switch (state) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
    case 'in-progress':
      return 'bg-blue-100 text-blue-800 ring-1 ring-blue-200';
    default:
      return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
  }
}

interface OrgGroupedTaskListProps {
  organizationActivities: OrganizationActivity[];
  onViewTask: (task: TaskWithContext) => void;
  emptyMessage?: string;
}

export function OrgGroupedTaskList({ organizationActivities, onViewTask, emptyMessage }: OrgGroupedTaskListProps) {
  if (organizationActivities.length === 0) {
    return (
      <div className="text-center py-20 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-2xl">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ListTodo className="h-8 w-8 text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium text-lg">{emptyMessage || 'No tasks found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {organizationActivities.map((orgActivity, orgIndex) => (
        <div
          key={orgActivity.orgId}
          className="bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
          style={{ animationDelay: `${orgIndex * 100}ms`, animation: 'fadeIn 0.6s ease-out' }}
        >
          {/* Organization Header */}
          <div className="p-6 bg-gradient-to-r from-slate-50 to-emerald-50/30 border-b border-slate-200/60">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">{orgActivity.orgName}</h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-slate-600 font-medium">
                    <span className="text-emerald-700 font-bold">{orgActivity.taskCount}</span> tasks
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Task List */}
          <div className="divide-y divide-slate-100">
            {orgActivity.tasks.map((task, taskIndex) => (
              <div
                key={task.taskId}
                className="p-5 hover:bg-slate-50/60 transition-colors group"
                style={{ animationDelay: `${taskIndex * 50}ms`, animation: 'fadeInSlide 0.4s ease-out' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 text-base mb-2 group-hover:text-emerald-700 transition-colors">
                      {task.name}
                    </h4>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-600">
                        <span className="font-medium text-slate-700">Trial:</span> {task.trialName}
                        <span className="text-slate-400 mx-2">&middot;</span>
                        <span className="font-medium text-slate-700">Experiment:</span> {task.experimentName}
                      </p>
                      <p className="text-sm text-slate-600">
                        <span className="font-medium text-slate-700">Trait:</span> {task.entryConfigName}
                        <span className="text-slate-400 mx-2">&middot;</span>
                        <span className="font-medium text-slate-700">Created by:</span> {task.createdBy.displayName}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 items-end shrink-0">
                    <span className={`px-3 py-1.5 text-xs font-bold rounded-full uppercase tracking-wide ${getTaskStateBadgeClass(task.state)}`}>
                      {task.state}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewTask(task)}
                      className="text-xs font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 transition-all group/btn"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5 group-hover/btn:translate-x-0.5 transition-transform" />
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
